/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import { useLocation } from 'js/lib/useRouter';

import type { EventingData } from 'bundles/search-common/components/filters/SearchFilterItem';
import SearchFilterItem from 'bundles/search-common/components/filters/SearchFilterItem';
import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import {
  MICRO_LESSONS_FACET_FILTERS,
  MICRO_LESSONS_FACET_KEY,
  MICRO_LESSONS_HIDDEN_FACET_KEYS,
  MICRO_LESSONS_PRODUCT_TYPE_VALUES,
} from 'bundles/search-common/constants/filter';
import useIsMicroLessonsRolloutEnabled from 'bundles/search-common/hooks/useIsMicroLessonsRolloutEnabled';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { StateUpdates } from 'bundles/search-common/providers/searchTypes';
import type { SearchFilterOption } from 'bundles/search-common/types';

import _t from 'i18n!nls/search-common';

type Props = {
  name: string;
  videoItem?: SearchFilterOption;
  lessonItem?: SearchFilterOption;
  eventingData: EventingData;
  onToggle: (isActive: boolean) => void;
  /**
   * Match the compact styling the generic filter groups apply to their own items — most visibly a
   * darker unchecked checkbox border (`--cds-color-grey-700` rather than the CDS default
   * `--cds-color-neutral-stroke-primary`). Defaults to true: every surface renders the toggle
   * alongside compact group items except the mobile modal, whose accordion items are not compact.
   */
  isCompact?: boolean;
};

const styles = {
  filterContainer: css`
    margin-bottom: 32px;
  `,
};

// SEARCH-2412: mirrors CourseraPlusSearchFilter - the two underlying productTypeDescription
// values Micro Lessons combines are pulled out of the generic Learning Product item list and
// rendered as one standalone toggle instead.
export const partitionMicroLessonsItems = (
  items: SearchFilterOption[] = []
): {
  videoItem?: SearchFilterOption;
  lessonItem?: SearchFilterOption;
  remainingItems: SearchFilterOption[];
} => {
  const [video, lesson] = MICRO_LESSONS_PRODUCT_TYPE_VALUES;
  const videoItem = items.find((item) => item.value === video);
  const lessonItem = items.find((item) => item.value === lesson);
  const remainingItems = items.filter((item) => !MICRO_LESSONS_PRODUCT_TYPE_VALUES.includes(item.value));
  return { videoItem, lessonItem, remainingItems };
};

// Active only when both underlying values are currently applied together, matching
// isMicroLessonsFilterActive in filters2024/utils.tsx. See useHasOrphanedMicroLessonsUrlValue for
// the one deliberate exception.
//
// Read from the applied filters rather than from the two Learning Product facet options: the facet
// response only carries values that still have results, so a query with short-form videos but no
// short-form lessons drops the Lesson option entirely. Deriving activeness from the options then
// reads an applied pair as "off", leaving the toggle unchecked while its pill shows above the
// results (SEARCH-2412).
export const isMicroLessonsPairApplied = (facetFilters?: string[][] | null): boolean => {
  const appliedFilters = facetFilters?.flat() ?? [];
  return MICRO_LESSONS_FACET_FILTERS.every((filter) => appliedFilters.includes(filter));
};

// SEARCH-2412: a URL saved or shared before the combined toggle shipped can carry just one of the
// two underlying values. Nothing in the UI can produce that state - the toggle only ever applies or
// clears the pair together, and the individual Video/Lesson checkboxes are always stripped from
// Learning Product - so treat a lone URL value as the toggle being on. Without this the value stays
// applied to the query with no visible control to see or clear it, and clicking the (apparently
// off) toggle would silently widen results instead of clearing them.
//
// Deliberately keyed off the URL rather than the applied filters: an exactly-one match in the
// applied filters can also come from a surface's own config (auto-applied or hidden filters), which
// should keep the strict both-values-required behavior above.
export const hasOrphanedMicroLessonsUrlValue = (
  urlQueryParams: Record<string, string | string[] | undefined> | undefined
): boolean => {
  const rawValue = urlQueryParams?.[MICRO_LESSONS_FACET_KEY];
  const urlValues = Array.isArray(rawValue) ? rawValue : [rawValue];
  return MICRO_LESSONS_PRODUCT_TYPE_VALUES.filter((value) => urlValues.includes(value)).length === 1;
};

// Hook form for the filter components, several of which derive activeness inside a useMemo or a
// conditional branch where a hook cannot be called - read this at component top level instead.
export const useHasOrphanedMicroLessonsUrlValue = (): boolean => {
  const { query } = useLocation();
  return hasOrphanedMicroLessonsUrlValue(query);
};

// Single source of truth for whether the combined toggle reads as on, shared by every surface that
// renders it and by the groups that hide themselves while it is active.
export const useIsMicroLessonsFilterActive = (searchIndexId: string = SEARCH_MAIN_INDEX_ID): boolean => {
  const { getSearchResults } = useSearch();
  const searchData = getSearchResults(searchIndexId);
  const hasOrphanedUrlValue = useHasOrphanedMicroLessonsUrlValue();
  const appliedFilters = searchData?.facetFilters ?? [];

  // getSearchResults returns no result at all for a beat while any filter update is in flight, and
  // an applied pair reads as "off" from a blank result - hasOrphanedUrlValue cannot cover that, as
  // it only fires for an exactly-one-value URL. Left unhandled it unchecks a filter that is still
  // applied and routes a click to the apply branch, which re-applies the pair and drops
  // productDuration. So fall back to the last state observed while results were present.
  //
  // Remembered as a boolean rather than through useStableSearchResults: keying the fallback on a
  // primitive keeps it independent of the identity of the object getSearchResults hands back, which
  // every caller providing a search context would otherwise have to hold stable across renders.
  const isPairApplied = searchData ? isMicroLessonsPairApplied(appliedFilters) : undefined;
  const [lastKnownPairApplied, setLastKnownPairApplied] = useState(false);

  useEffect(() => {
    if (isPairApplied !== undefined) {
      setLastKnownPairApplied(isPairApplied);
    }
  }, [isPairApplied]);

  return (isPairApplied ?? lastKnownPairApplied) || hasOrphanedUrlValue;
};

// Facet groups a surface must hide while the toggle is on: Duration and Learning Product are both
// meaningless once results are scoped to micro-lesson content. Only for surfaces that render the
// toggle above their groups (see MicroLessonsFilterGroup) - where the toggle still lives inside the
// Learning Product group, hiding that group would take the toggle with it.
export const useMicroLessonsHiddenFacetKeys = (searchIndexId: string = SEARCH_MAIN_INDEX_ID): string[] => {
  const isRolloutEnabled = useIsMicroLessonsRolloutEnabled();
  const isActive = useIsMicroLessonsFilterActive(searchIndexId);

  return isRolloutEnabled && isActive ? MICRO_LESSONS_HIDDEN_FACET_KEYS : [];
};

// The one apply/clear payload every surface sends, so a toggle inside a Learning Product group and
// one rendered as its own group cannot drift apart.
//
// `page` is set explicitly because narrowing or widening the result set invalidates the current
// offset and the learner belongs back on the first page. The provider's own addFilters/
// removeFilters/setFiltersByFacet all reset page for this reason; applySearchStateUpdates keys off
// the presence of the key, so an omitted `page` leaves the learner stranded on page 3 of a shorter
// result set (SEARCH-2412 review).
export const buildMicroLessonsToggleUpdates = (nextActive: boolean): StateUpdates => ({
  setFiltersByFacet: {
    [MICRO_LESSONS_FACET_KEY]: nextActive ? MICRO_LESSONS_FACET_FILTERS : [],
    ...(nextActive && { productDuration: [] }),
  },
  page: undefined,
});

const MicroLessonsSearchFilter = ({ name, videoItem, lessonItem, eventingData, onToggle, isCompact = true }: Props) => {
  const isActive = useIsMicroLessonsFilterActive();

  // Stay mounted while active even if the facet response no longer carries either short-form
  // value, so there is always a control to clear the filter with.
  if (!videoItem && !lessonItem && !isActive) {
    return null;
  }

  const count = (videoItem?.count ?? 0) + (lessonItem?.count ?? 0);

  return (
    <div className="rc-MicroLessonsSearchFilter" css={styles.filterContainer}>
      <SearchFilterItem
        value="isMicroLessons:true"
        label={_t('Micro Lessons')}
        count={count}
        isRefined={isActive}
        filterCategoryName={name}
        addFilters={() => onToggle(true)}
        removeFilters={() => onToggle(false)}
        eventingData={eventingData}
        customAriaLabel={_t('Micro Lessons')}
        isCompact={isCompact}
      />
    </div>
  );
};

type MicroLessonsFilterGroupProps = {
  /** The Learning Product (productTypeDescription) facet options the toggle draws its values from. */
  items?: SearchFilterOption[];
  eventingData: EventingData;
  onFilterValueChange?: () => void;
  searchIndexId?: string;
  /** See MicroLessonsSearchFilter's isCompact. */
  isCompact?: boolean;
};

// SEARCH-2412: the standalone form of the toggle, for the inline sidebars that render it as its own
// group above every filter group rather than buried inside Learning Product. Owns the apply/clear
// payload so those surfaces cannot drift apart.
export const MicroLessonsFilterGroup = ({
  items,
  eventingData,
  onFilterValueChange,
  searchIndexId = SEARCH_MAIN_INDEX_ID,
  isCompact,
}: MicroLessonsFilterGroupProps) => {
  const { applySearchStateUpdates } = useSearch();
  const isRolloutEnabled = useIsMicroLessonsRolloutEnabled();
  const isActive = useIsMicroLessonsFilterActive(searchIndexId);
  const { videoItem, lessonItem } = partitionMicroLessonsItems(items);

  if (!isRolloutEnabled || !(videoItem || lessonItem || isActive)) {
    return null;
  }

  return (
    <MicroLessonsSearchFilter
      name={_t('Micro Lessons')}
      videoItem={videoItem}
      lessonItem={lessonItem}
      eventingData={eventingData}
      isCompact={isCompact}
      onToggle={(nextActive) => {
        applySearchStateUpdates(searchIndexId, buildMicroLessonsToggleUpdates(nextActive));
        onFilterValueChange?.();
      }}
    />
  );
};

export default MicroLessonsSearchFilter;
